<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import languages, {
  getLanguageName,
} from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import authAPI from 'dashboard/api/auth';

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');

const codeToName = Object.fromEntries(languages.map(l => [l.id, l.name]));
const nameToCode = Object.fromEntries(languages.map(l => [l.name, l.id]));

// Local state for tags (array of language name strings)
const tagLabels = ref([]);
const isSaving = ref(false);

// Sync store → local on mount and when store changes externally
watch(
  () => currentUser.value?.languages,
  langs => {
    if (isSaving.value) return; // don't overwrite during save
    tagLabels.value = (langs || []).map(
      code => codeToName[code] || getLanguageName(code) || code
    );
  },
  { immediate: true }
);

const menuItems = computed(() => {
  const selectedNames = new Set(tagLabels.value);
  return languages
    .filter(lang => !selectedNames.has(lang.name))
    .map(lang => ({
      action: lang.id,
      value: lang.id,
      label: lang.name,
    }));
});

const saveLanguages = async () => {
  isSaving.value = true;
  try {
    const codes = tagLabels.value.map(name => nameToCode[name] || name);
    await authAPI.updateLanguages(codes);
    // Use targeted mutation — NOT SET_CURRENT_USER which triggers full page re-render
    store.commit('SET_CURRENT_USER_LANGUAGES', { languages: codes });
    useAlert(
      t(
        'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.SPOKEN_LANGUAGES.UPDATE_SUCCESS'
      )
    );
  } catch {
    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.SPOKEN_LANGUAGES.UPDATE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const handleAdd = () => {
  nextTick(() => saveLanguages());
};

const handleRemove = () => {
  nextTick(() => saveLanguages());
};
</script>

<template>
  <div class="flex flex-col gap-2 w-full">
    <label class="text-sm font-medium text-n-slate-12">
      {{ $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.SPOKEN_LANGUAGES.TITLE') }}
    </label>
    <p class="text-sm text-n-slate-11">
      {{ $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.SPOKEN_LANGUAGES.NOTE') }}
    </p>
    <TagInput
      v-model="tagLabels"
      :menu-items="menuItems"
      :placeholder="
        $t(
          'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.SPOKEN_LANGUAGES.PLACEHOLDER'
        )
      "
      show-dropdown
      :auto-open-dropdown="false"
      mode="MULTIPLE"
      @add="handleAdd"
      @remove="handleRemove"
    />
  </div>
</template>
